/**
 * customFetch.js
 * Wrapper de fetch con renovación automática de token (refresh token).
 * - Adjunta el Bearer token en cada request
 * - Si recibe 401, renueva el token y reintenta UNA sola vez (anti loop)
 * - Si el refresh falla limpiamente, cierra la sesión y redirige a /login
 */

import { useAuth } from 'Contextos/AuthContext';

// ── Control de refresh en curso ───────────────────────────────
let isRefreshing = false;
let pendingQueue = [];

function processQueue(error, token = null) {
    pendingQueue.forEach((p) => {
        if (error) {
            p.reject(error);
        } else {
            p.resolve(token);
        }
    });
    pendingQueue = [];
}

/**
 * @param {string}  url     – URL completa del endpoint
 * @param {object}  config  – Configuración del fetch (method, headers, body, etc.)
 * @param {boolean} _retry  – Flag interno: true si esta llamada ya es un reintento
 *                             post-refresh. Evita loops infinitos destructivos.
 */
const customFetch = async (url, config, _retry = false) => {
    // 1. Añadimos el Access Token actual (Bearer) a los headers
    const JWT = JSON.parse(localStorage.getItem('JWT'));

    // Si no hay headers predefinidos, creamos uno, si lo hay, lo clonamos.
    let headers = { ...config.headers };
    if (JWT && !headers.Authorization) {
        headers.Authorization = JWT;
    }

    const modifiedConfig = { ...config, headers };

    try {
        // Ejecutamos la petición original
        let response = await fetch(url, modifiedConfig);

        // Si la respuesta es exitosa o diferente de 401, simplemente la devolvemos
        if (response.status !== 401) {
            return response;
        }

        // ── ANTI-LOOP: Si esta petición ya es un retry post-refresh, no
        //    volvemos a intentar refresh. Vamos directo a forceLogout.
        if (_retry) {
            const retryError = new Error('Reintento post-refresh falló nuevamente con 401');
            retryError.isAuthError = true;
            throw retryError;
        }

        // Si ya estamos intentando refrescar, ponemos la peticion en la cola
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                pendingQueue.push({ resolve, reject });
            }).then((newToken) => {
                // Una vez resuelto, volvemos a intentar pero con el header nuevo
                // y pasamos TRUE para que si falla con 401 no estalle el loop
                const newHeaders = { ...modifiedConfig.headers, Authorization: newToken };
                return customFetch(url, { ...modifiedConfig, headers: newHeaders }, true);
            });
        }

        // Bloqueamos para evitar multiples llamadas al Refresh HTTP
        isRefreshing = true;

        const refreshToken = JSON.parse(localStorage.getItem('refreshToken'));

        // Si no tenemos refresh token, enviamos al login
        if (!refreshToken) {
            isRefreshing = false;
            return forceLogout(response);
        }

        // Generamos la llamada para refrescar token 
        const URL_AUTH = process.env.REACT_APP_HOST_API_AUTH;

        const refreshResponse = await fetch(`${URL_AUTH}Login/Refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken })
        });

        if (!refreshResponse.ok) {
            const refreshError = new Error('Refresh endpoint respondió con error');
            refreshError.isAuthError = true;
            throw refreshError;
        }

        const data = await refreshResponse.json();

        // Validamos la estructura del JSON devuelto basado en nuestro cambio de backend
        if (data.code === "1" && data.payload && data.payload.accessToken) {
            const newToken = "Bearer " + data.payload.accessToken;
            const newRefreshToken = data.payload.refreshToken;

            // Actualizamos firmemente en local storage
            localStorage.setItem('JWT', JSON.stringify(newToken));
            if (newRefreshToken) {
                localStorage.setItem('refreshToken', JSON.stringify(newRefreshToken));
            }

            // Resolvemos todas las peticiones encoladas con el nuevo token
            processQueue(null, newToken);
            isRefreshing = false;

            // Reintentamos la petición fallida original con el nuevo token
            // pasamos = true para que actue con sabiduría y no cause un loop
            const retryHeaders = { ...modifiedConfig.headers, Authorization: newToken };
            return await customFetch(url, { ...modifiedConfig, headers: retryHeaders }, true);
        } else {
            const invalidPayloadError = new Error('Respuesta de refresh con payload inválido');
            invalidPayloadError.isAuthError = true;
            throw invalidPayloadError;
        }

    } catch (error) {
        // Asegurarnos que un problema netamente de falta de Internet temporal
        // no asesine la sesión del usuario a menos que sea error neto del login
        const isAuthError = error.isAuthError || error.message.includes('refresh') || error.message.includes('401');

        // Rechazamos todas las peticiones en cola con el error
        if (isRefreshing) {
            processQueue(error, null);
        }
        isRefreshing = false;

        // PROTECCIÓN OFFLINE TIPO PWA: Si no es un error de JWT sino que tu antena Wifi tardó
        // en despertar, que solo propague o ignore el error pero sin cerrar todo.
        if (!isAuthError && error.name === 'TypeError') {
            throw error;
        }

        // El refresh ha fallado definitivamente genuinamente. Requerimos cerrar
        return forceLogout(error);
    }
};

const forceLogout = (errorValue) => {
    // Eliminamos sesion completamente
    localStorage.clear();
    // Prevenimos que se salte la segurizacion del app
    localStorage.setItem('go', true);

    // Recargamos hacia login
    window.location.href = '/';

    if (errorValue instanceof Error) throw errorValue;
    return errorValue;
}

export default customFetch;
